






































































import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import InfoConfigProblemListComponent from "@/components/admin/infoConfig/InfoConfigProblemList.vue";
import InfoConfigDifferenceListComponent from "@/components/admin/infoConfig/InfoConfigDifferenceList.vue";
import { InfoConfigCheckResult, InfoConfigProblem, InfoConfigVersionRow } from "@/data/models/InfoConfigAdministration";
import { checkInfoConfigUpload, publishInfoConfigVersion } from "@/data/repository/InfoConfigAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  infoConfigErrorKey,
  MAX_INFO_CONFIG_BYTES,
  splitProblems,
  suggestNextVersion,
  versionFromFileName,
  versionInputProblem,
} from "@/utils/InfoConfigAdministration";
import { readFileAsBase64 } from "@/utils/PdfTemplateFiles";

/**
 * Publishing a new Beschreibung version: a CSV and its number; PRÜFEN has IRES Cloud check it and list
 * its differences from the global target; VERÖFFENTLICHEN is possible only for exactly the file and
 * number that were checked, with no error and every warning acknowledged. A published version can never
 * be changed, which the dialog says up front. IRES Cloud runs the check again before it writes.
 *
 * With a preset (a draft), the file and its number come from the draft: no file to pick, the number
 * fixed (the draft's `#Version` line names it).
 *
 * `show()` resolves with the published version, or null.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "problem-list": InfoConfigProblemListComponent,
    "difference-list": InfoConfigDifferenceListComponent,
  },
})
export default class InfoConfigPublishDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  busy = false;
  file: File | null = null;
  contentBase64 = "";
  version = "";
  rows: InfoConfigVersionRow[] = [];
  result: InfoConfigCheckResult | null = null;
  /** The file and number the result belongs to; any change invalidates it. */
  checkedKey = "";
  acknowledged = false;
  /** The draft's name when the content comes from a draft; empty for an uploaded file. */
  presetLabel = "";

  private resolve: ((version: string | null) => void) | null = null;

  show(
    rows: InfoConfigVersionRow[],
    preset: { contentBase64: string; version: string; label: string } | null = null
  ): Promise<string | null> {
    this.rows = rows;
    this.file = null;
    this.contentBase64 = preset?.contentBase64 ?? "";
    this.version = preset?.version ?? suggestNextVersion(rows);
    this.presetLabel = preset?.label ?? "";
    this.result = null;
    this.checkedKey = "";
    this.acknowledged = false;
    // Persistent, and neither PRÜFEN nor VERÖFFENTLICHEN closes it by itself: a publish closes it once done.
    this.baseDialog.show({
      title: this.$t("admin.infoConfig.publish.title").toString(),
      positiveButtonText: this.$t("admin.infoConfig.publish.publish").toString(),
      neutralPositiveButtonText: this.$t("admin.infoConfig.publish.check").toString(),
      negativeButtonText: this.$t("common.abort").toString(),
      maxWidth: 860,
      scrollable: true,
      persistent: true,
      closeOnSave: false,
      closeOnNeutralPositiveAction: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  get currentKey(): string {
    // The content itself, not its length: a different file of the same size must be checked again.
    return `${this.version.trim()}|${this.contentBase64}`;
  }

  get fileError(): string | null {
    return this.file !== null && this.file.size > MAX_INFO_CONFIG_BYTES
      ? this.$t("admin.infoConfig.errors.FILE_TOO_LARGE").toString()
      : null;
  }

  get versionError(): string | null {
    const problem = versionInputProblem(this.version, this.rows);
    return problem ? this.$t(`admin.infoConfig.publish.versionProblems.${problem}`).toString() : null;
  }

  get errors(): InfoConfigProblem[] {
    return this.result ? splitProblems(this.result.problems).errors : [];
  }

  get warnings(): InfoConfigProblem[] {
    return this.result ? splitProblems(this.result.problems).warnings : [];
  }

  get canCheck(): boolean {
    return this.contentBase64 !== "" && this.fileError === null && this.versionError === null;
  }

  get canPublish(): boolean {
    return (
      this.result !== null &&
      this.checkedKey === this.currentKey &&
      this.errors.length === 0 &&
      (this.warnings.length === 0 || this.acknowledged)
    );
  }

  async onFileChanged(file: File | null) {
    this.file = file ?? null;
    this.contentBase64 = file ? await readFileAsBase64(file) : "";
    const named = file ? versionFromFileName(file.name) : null;
    if (named) {
      this.version = named;
    }
    this.acknowledged = false;
  }

  onVersionChanged(version: string) {
    this.version = version ?? "";
    this.acknowledged = false;
  }

  async onCheck() {
    this.busy = true;
    try {
      this.result = await checkInfoConfigUpload(this.version.trim(), this.contentBase64, null);
      this.checkedKey = this.currentKey;
      this.acknowledged = false;
    } catch (error) {
      SnackbarModule.showError({ key: infoConfigErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  async onPublish() {
    if (!this.canPublish) {
      return;
    }
    this.busy = true;
    try {
      await publishInfoConfigVersion(this.version.trim(), this.contentBase64, this.acknowledged);
      SnackbarModule.showSuccess({ key: "admin.infoConfig.publish.published", params: [this.version.trim()] });
      this.close(this.version.trim());
    } catch (error) {
      SnackbarModule.showError({ key: infoConfigErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  /** BaseDialog has already closed itself on ABBRECHEN. */
  onCancel() {
    this.resolve?.(null);
    this.resolve = null;
  }

  close(version: string | null) {
    this.baseDialog.isShowing = false;
    this.resolve?.(version);
    this.resolve = null;
  }
}
